import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import * as StompJs from "@stomp/stompjs";
import SockJS from "sockjs-client";
import {
  handleAbacPermissionsPushEvent,
  loadAbacSessionPermissions,
} from "../components/Auth/AuthSlice";
import { isMultiAccessUser } from "../components/User/Abac/abacAccess";

const ABAC_PERMISSIONS_QUEUE = "/user/queue/abac-permissions";

const WebSocketNotificationContext = createContext(null);

export const useWebSocketNotification = () => {
  const context = useContext(WebSocketNotificationContext);

  return (
    context || {
      connected: false,
      connect: () => {},
      disconnect: () => {},
      registerEventHandler: () => () => {},
      fetchNotifications: () => {},
      deleteNotification: () => {},
      deleteAllNotifications: () => {},
      updateNotification: () => {},
      pendingMutationRef: { current: false },
    }
  );
};

const eventHandlers = new Set();

export const WebSocketNotificationProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const clientRef = useRef(null);
  const connectingRef = useRef(false);
  const abacSubscriptionRef = useRef(null);
  const subscriptionRef = useRef(null); // ✅ track active subscription — prevents duplicate messages

  const pendingMutationRef = useRef(false);

  const dispatch = useDispatch();
  const user = useSelector((state) => state?.auth?.user);
  const isLoggedIn = !!user?.accessToken;
  const abacSessionLoaded = useSelector(
    (state) => state?.auth?.abacSessionLoaded
  );
  const isAbacMember = isLoggedIn && isMultiAccessUser(user);

  // Websocket pushes only arrive on change, so load the saved permissions once.
  useEffect(() => {
    if (isAbacMember && !abacSessionLoaded) {
      dispatch(loadAbacSessionPermissions());
    }
  }, [isAbacMember, abacSessionLoaded, user?.accessToken, dispatch]);

  const registerEventHandler = useCallback((handler) => {
    eventHandlers.add(handler);
    return () => eventHandlers.delete(handler);
  }, []);

  const notifyHandlers = useCallback((data) => {
    eventHandlers.forEach((handler) => {
      try {
        handler(data);
      } catch (_) {}
    });
  }, []);

  const handleIncomingMessage = useCallback(
    (data) => {
      const responseType = (data.type || data.requestType || "").toUpperCase();

      if (responseType.startsWith("LIVE")) {
        // console.log("🟢 ========================================");
        // console.log("🟢 [Notification WebSocket] Ye data mila hai - LIVE type");
        // console.log("🟢 [Notification WebSocket] Live notification mil gayi hai");
        // console.log("🟢 [Notification WebSocket] Notifications count:", data.notifications?.length || 0);
        // console.log("🟢 [Notification WebSocket] Notifications data:", data.notifications);
        // console.log("🟢 [Notification WebSocket] systemId:", data.systemId || "null");
        // console.log("🟢 [Notification WebSocket] Full data:", JSON.stringify(data, null, 2));
        // console.log("🟢 ========================================");
        notifyHandlers({
          type: "live",
          notifications: data.notifications || [],
          requestId: data.requestId || null,
          systemId: data.systemId || null,
        });
        return;
      }

      switch (responseType) {
        case "DATA":
          if (
            typeof data.requestId === "string" &&
            data.requestId.startsWith("live-")
          ) {
            notifyHandlers({
              type: "live",
              notifications: data.notifications || [],
              requestId: data.requestId || null,
              systemId: data.systemId || null,
            });
            break;
          }
        //   console.log("📥 ========================================");
        //   console.log("📥 [Notification WebSocket] Ye data mila hai - DATA type");
        //   console.log("📥 [Notification WebSocket] Notifications count:", data.notifications?.length || 0);
        //   console.log("📥 [Notification WebSocket] Notifications data:", data.notifications);
        //   console.log("📥 ========================================");
          notifyHandlers({
            type: "fetch",
            notifications: data.notifications || [],
            requestId: data.requestId || null,
            systemId: data.systemId || null,
          });
          break;

        case "SUCCESS":
        //   console.log("✅ ========================================");
        //   console.log("✅ [Notification WebSocket] Ye data mila hai - SUCCESS type");
        //   console.log("✅ [Notification WebSocket] Operation successful:", data.message || "no message");
        //   console.log("✅ [Notification WebSocket] requestId:", data.requestId || "null");
        //   console.log("✅ [Notification WebSocket] Full data:", JSON.stringify(data, null, 2));
        //   console.log("✅ ========================================");
          notifyHandlers({
            type: "success",
            message: data.message || null,
            requestId: data.requestId || null,
            systemId: data.systemId || null,
          });
          break;

        case "ERROR":
          console.error("❌ ========================================");
          console.error("❌ [Notification WebSocket] Ye data mila hai - ERROR type");
          console.error("❌ [Notification WebSocket] Error aaya hai:", data.message || data.errorCode || "Unknown error");
          console.error("❌ [Notification WebSocket] requestId:", data.requestId || "null");
          console.error("❌ [Notification WebSocket] Full data:", JSON.stringify(data, null, 2));
          console.error("❌ ========================================");
          notifyHandlers({
            type: "error",
            errorCode: data.errorCode || null,
            message: data.message || data.errorCode || "Unknown error",
            requestId: data.requestId || null,
            systemId: data.systemId || null,
          });
          break;

        default:
          console.warn("⚠️ [Notification WebSocket] Unknown response type:", responseType);
          console.warn("⚠️ [Notification WebSocket] Full data:", JSON.stringify(data, null, 2));
          break;
      }
    },
    [notifyHandlers]
  );

  const publish = useCallback((payload) => {
    if (!clientRef.current || !clientRef.current.connected) {
      console.warn("⚠️ [Notification WebSocket] Not connected, skipping publish");
      return;
    }
    try {
      clientRef.current.publish({
        destination: "/app/fetchNotification",
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error("❌ [Notification WebSocket] Publish error:", error);
    }
  }, []);

  const fetchNotifications = useCallback(() => {
    const payload = {
      requestType: "FETCH",
      requestId: `fetch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    // console.log("📤 ========================================");
    // console.log("📤 [Notification WebSocket] FETCH request bhej rahe hain");
    // console.log("📤 [Notification WebSocket] Payload:", JSON.stringify(payload, null, 2));
    // console.log("📤 ========================================");
    publish(payload);
  }, [publish]);

  const deleteNotification = useCallback(
    (id) => {
      pendingMutationRef.current = true;
      const payload = {
        requestType: "DELETE",
        requestId: `delete-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        id,
      };
    //   console.log("🗑️ ========================================");
    //   console.log("🗑️ [Notification WebSocket] DELETE request bhej rahe hain");
    //   console.log("🗑️ [Notification WebSocket] Notification ID:", id);
    //   console.log("🗑️ [Notification WebSocket] Payload:", JSON.stringify(payload, null, 2));
    //   console.log("🗑️ ========================================");
      publish(payload);
    },
    [publish]
  );

  const deleteAllNotifications = useCallback(() => {
    pendingMutationRef.current = true;
    const payload = {
      requestType: "DELETE_ALL",
      requestId: `delall-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
    // console.log("🗑️ ========================================");
    // console.log("🗑️ [Notification WebSocket] DELETE_ALL request bhej rahe hain");
    // console.log("🗑️ [Notification WebSocket] Payload:", JSON.stringify(payload, null, 2));
    // console.log("🗑️ ========================================");
    publish(payload);
  }, [publish]);

  const updateNotification = useCallback(
    (id, read, reference = null) => {
      pendingMutationRef.current = true;
      const payload = {
        requestType: "UPDATE",
        requestId: `update-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        id,
        read,
        reference,
      };
    //   console.log("✏️ ========================================");
    //   console.log("✏️ [Notification WebSocket] UPDATE request bhej rahe hain");
    //   console.log("✏️ [Notification WebSocket] Notification ID:", id);
    //   console.log("✏️ [Notification WebSocket] Read:", read, "| Reference:", reference);
    //   console.log("✏️ [Notification WebSocket] Payload:", JSON.stringify(payload, null, 2));
    //   console.log("✏️ ========================================");
      publish(payload);
    },
    [publish]
  );

  const connect = useCallback(() => {
    if (!isLoggedIn) return;
    if (clientRef.current?.active) return;
    if (connectingRef.current) return;

    connectingRef.current = true;

    const apiUrl = import.meta.env.VITE_REACT_APP_API_URL || "";

    let wsUrl = "";
    if (apiUrl) {
      wsUrl = apiUrl.replace(/\/+$/, "") + "/notification-socket";
    } else {
      console.error(
        "❌ [Notification WebSocket] No URL configured. Set VITE_REACT_APP_API_URL in .env"
      );
      connectingRef.current = false;
      return;
    }

    // Prefer live auth token so a role switch never reuses the previous session
    let token = user?.accessToken || null;
    if (!token) {
      try {
        const persistRoot = localStorage.getItem("persist:root");
        if (persistRoot) {
          const rootData = JSON.parse(persistRoot);
          if (rootData.auth) {
            const authData = JSON.parse(rootData.auth);
            if (authData.user?.accessToken) {
              token = authData.user.accessToken;
            }
          }
        }
      } catch (error) {
        console.error("❌ [Notification WebSocket] Token extraction error:", error);
      }
    }

    clientRef.current = new StompJs.Client({
      webSocketFactory: () => new SockJS(wsUrl),
      connectHeaders: {
        Authorization: token ? `Bearer ${token}` : "",
      },
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      reconnectDelay: 3000,
      connectionTimeout: 15000,
      debug: () => {},

      onConnect: () => {
        connectingRef.current = false;
        setConnected(true);

        try {
          // ✅ Unsubscribe old subscription before creating new one
          // Prevents duplicate message delivery on reconnect
          if (subscriptionRef.current) {
            try { subscriptionRef.current.unsubscribe(); } catch (_) {}
            subscriptionRef.current = null;
          }

          subscriptionRef.current = clientRef.current.subscribe(
            "/user/topic/notifications",
            (message) => {
              if (!message?.body) return;
              try {
                const data = JSON.parse(message.body);
                handleIncomingMessage(data);
              } catch (e) {
                console.error("❌ [Notification WebSocket] Message parse error:", e);
              }
            }
          );

          // Live ABAC permission updates — backend only sends these to multi-access member principals.
          if (abacSubscriptionRef.current) {
            try { abacSubscriptionRef.current.unsubscribe(); } catch (_) {}
          }
          abacSubscriptionRef.current = clientRef.current.subscribe(
            ABAC_PERMISSIONS_QUEUE,
            (message) => {
              if (!message?.body) {
                console.log("[ABAC WS] empty message received", message);
                return;
              }
              try {
                const event = JSON.parse(message.body);
                console.log("[ABAC WS] event received:", event);
                dispatch(handleAbacPermissionsPushEvent(event));
              } catch (e) {
                console.error("[ABAC WS] parse error:", e, message.body);
              }
            }
          );
          console.log("[ABAC WS] subscribed to", ABAC_PERMISSIONS_QUEUE);

          setTimeout(() => {
            if (!clientRef.current?.connected) return;
            clientRef.current.publish({
              destination: "/app/fetchNotification",
              body: JSON.stringify({
                requestType: "FETCH",
                requestId: `fetch-${Date.now()}-${Math.random()
                  .toString(36)
                  .substr(2, 9)}`,
              }),
            });
          }, 300);
        } catch (error) {
          console.error("❌ [Notification WebSocket] Subscribe error:", error);
        }
      },

      onDisconnect: (frame) => {
        connectingRef.current = false;
        abacSubscriptionRef.current = null;
        subscriptionRef.current = null; // ✅ clear ref on disconnect
        // console.error("🔴 ========================================");
        // console.error("🔴 [Notification WebSocket] DISCONNECT hua");
        // console.error("🔴 [Notification WebSocket] Frame:", JSON.stringify(frame));
        // console.error("🔴 ========================================");
        setConnected(false);
      },
      onStompError: (frame) => {
        connectingRef.current = false;
        // console.error("❌ ========================================");
        // console.error("❌ [Notification WebSocket] STOMP ERROR");
        // console.error("❌ [Notification WebSocket] Headers:", JSON.stringify(frame.headers));
        // console.error("❌ [Notification WebSocket] Body:", frame.body);
        // console.error("❌ ========================================");
      },
      onWebSocketError: (error) => {
        connectingRef.current = false;
        console.error("❌ [Notification WebSocket] WebSocket error:", error);
      },
    });

    clientRef.current.activate();
  }, [isLoggedIn, user?.accessToken, handleIncomingMessage, dispatch]);

  const disconnect = useCallback(() => {
    // ✅ Unsubscribe before deactivating
    if (subscriptionRef.current) {
      try { subscriptionRef.current.unsubscribe(); } catch (_) {}
      subscriptionRef.current = null;
    }
    if (abacSubscriptionRef.current) {
      try { abacSubscriptionRef.current.unsubscribe(); } catch (_) {}
      abacSubscriptionRef.current = null;
    }
    if (clientRef.current) {
      try {
        clientRef.current.deactivate();
      } catch (_) {}
      // Drop client so next login always opens a fresh socket with the new token
      clientRef.current = null;
    }
    connectingRef.current = false;
    pendingMutationRef.current = false;
    setConnected(false);
    // Clear stale badge across logout / role switch before the next user fetches
    if (typeof globalThis !== "undefined") {
      globalThis.notificationCount = 0;
    }
    notifyHandlers({ type: "session-reset" });
  }, [notifyHandlers]);

  const sessionKey = user?.accessToken || null;
  const prevSessionKeyRef = useRef(null);

  useEffect(() => {
    if (isLoggedIn && sessionKey) {
      // Token/user changed while a socket existed — tear down first so we never
      // keep serving the previous role's connection.
      if (
        prevSessionKeyRef.current &&
        prevSessionKeyRef.current !== sessionKey &&
        clientRef.current
      ) {
        disconnect();
      }
      prevSessionKeyRef.current = sessionKey;
      connect();
    } else {
      prevSessionKeyRef.current = null;
      disconnect();
    }

    return () => {
      if (!isLoggedIn && clientRef.current?.active) {
        disconnect();
      }
    };
  }, [isLoggedIn, sessionKey]);

  return (
    <WebSocketNotificationContext.Provider
      value={{
        connected,
        connect,
        disconnect,
        registerEventHandler,
        fetchNotifications,
        deleteNotification,
        deleteAllNotifications,
        updateNotification,
        pendingMutationRef,
      }}
    >
      {children}
    </WebSocketNotificationContext.Provider>
  );
};